import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Input,
  Modal,
  Popconfirm,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LinkOutlined,
  PlusOutlined,
  ShareAltOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { protectedApi } from "../../../app/utils/common/protectedApi";
import { APIS } from "../../../app/utils/constants/apiConstant";
import {
  canAccessProjectCredentialsPage,
  canCreateProjectCredentials,
  canDeleteProjectCredentials,
  canDeleteProjectCredentialFiles,
  canDeleteSharedCredentialFiles,
  canManagePersonalCredentials,
  canReadProjectCredentials,
  canReadProjectCredentialFiles,
  canReadSharedCredentialFiles,
  canShareProjectCredentials,
  canUpdateProjectCredentials,
  canUploadProjectCredentialFiles,
  canUploadSharedCredentialFiles,
  hasAdminAccess,
  showErrorResponse,
} from "../../../app/utils/common/Functions";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import {
  downloadProjectCredentialFile,
  FILE_TYPE_ACCEPT,
  FILE_TYPE_LABELS,
  FILE_TYPE_OPTIONS,
} from "../../../app/utils/common/projectCredentialFile";
import ProjectSelect from "../../atoms/ProjectSelect/ProjectSelect";
import "./ProjectCredentialsPage.scss";

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

const EMPTY_CREDENTIAL = {
  label: "",
  identifierType: "username",
  identifierValue: "",
  identifierOtherLabel: "",
  password: "",
  url: "",
  notes: "",
};
const EMPTY_INSTRUCTION = { title: "", content: "" };
const EMPTY_RESOURCE = { title: "", url: "", description: "" };
const EMPTY_FILE_FORM = { title: "", fileType: "env", notes: "", otherDescription: "" };

const IDENTIFIER_TYPE_OPTIONS = [
  { value: "phone", label: "Phone number" },
  { value: "username", label: "Username" },
  { value: "email", label: "Email" },
  { value: "other", label: "Other" },
];

const IDENTIFIER_TYPE_LABELS = {
  phone: "Phone number",
  username: "Username",
  email: "Email",
  other: "Other",
};

const identifierTypeDisplay = (type, otherLabel) => {
  if (type === "other") {
    const custom = String(otherLabel || "").trim();
    return custom || IDENTIFIER_TYPE_LABELS.other;
  }
  return IDENTIFIER_TYPE_LABELS[type] || IDENTIFIER_TYPE_LABELS.username;
};

const identifierPlaceholder = (type) => {
  if (type === "phone") return "e.g. +1 555 123 4567";
  if (type === "email") return "e.g. user@company.com";
  if (type === "other") return "e.g. value or key";
  return "e.g. admin_user";
};

const copyToClipboard = async (value) => {
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
    showSuccessToast("Copied to clipboard.");
  } catch (_e) {
    showErrorToast("Unable to copy.");
  }
};

const PasswordCell = ({ record, onReveal }) => {
  const [visible, setVisible] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const hasPassword = record.hasPassword ?? record.hasSecret;

  if (!hasPassword) {
    return <Text type="secondary">—</Text>;
  }

  const handleReveal = async () => {
    if (visible) {
      setVisible(false);
      return;
    }
    if (password) {
      setVisible(true);
      return;
    }
    setLoading(true);
    try {
      const value = await onReveal(record);
      setPassword(value || "");
      setVisible(true);
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="project-credentials-page__secret-cell">
      {visible ? (
        <Text code className="project-credentials-page__secret-value">
          {password || "—"}
        </Text>
      ) : (
        <Text type="secondary">••••••••</Text>
      )}
      <Button
        type="text"
        size="small"
        icon={visible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
        loading={loading}
        onClick={handleReveal}
        aria-label={visible ? "Hide password" : "Reveal password"}
      />
      {visible && password ? (
        <Button
          type="text"
          size="small"
          icon={<CopyOutlined />}
          onClick={() => copyToClipboard(password)}
          aria-label="Copy password"
        />
      ) : null}
    </div>
  );
};

const ProjectCredentialsPage = () => {
  const { user } = useSelector((state) => state.auth.auth);
  const canRead = hasAdminAccess(user) || canReadProjectCredentials();
  const canCreate = canCreateProjectCredentials(user);
  const canUpdate = canUpdateProjectCredentials(user);
  const canDelete = canDeleteProjectCredentials(user);
  const canPersonal = canManagePersonalCredentials(user);
  const canShareCredential = canShareProjectCredentials(user);
  const canReadProjectFiles = canReadProjectCredentialFiles(user);
  const canReadSharedFiles = canReadSharedCredentialFiles(user);
  const canUploadProjectFiles = canUploadProjectCredentialFiles(user);
  const canUploadSharedFiles = canUploadSharedCredentialFiles(user);
  const canDeleteProjectFiles = canDeleteProjectCredentialFiles(user);
  const canDeleteSharedFiles = canDeleteSharedCredentialFiles(user);
  const canViewFiles = canReadProjectFiles || canReadSharedFiles;
  const canViewPage = canAccessProjectCredentialsPage(user);

  const [activeTab, setActiveTab] = useState("credentials");
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);

  const [credentials, setCredentials] = useState([]);
  const [instructions, setInstructions] = useState([]);
  const [resources, setResources] = useState([]);
  const [projectFiles, setProjectFiles] = useState([]);
  const [personalCredentials, setPersonalCredentials] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [modalType, setModalType] = useState("credential");
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(EMPTY_CREDENTIAL);
  const [saving, setSaving] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [downloadLoadingId, setDownloadLoadingId] = useState(null);

  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [fileModalMode, setFileModalMode] = useState("create");
  const [fileModalScope, setFileModalScope] = useState("project");
  const [fileListFilter, setFileListFilter] = useState("all");
  const [uploadTargetProject, setUploadTargetProject] = useState(null);
  const [projectLinkMode, setProjectLinkMode] = useState("list");
  const [customReference, setCustomReference] = useState("");
  const [editingFile, setEditingFile] = useState(null);
  const [fileFormData, setFileFormData] = useState(EMPTY_FILE_FORM);
  const [fileUpload, setFileUpload] = useState(null);
  const [fileSaving, setFileSaving] = useState(false);
  const fileInputRef = useRef(null);

  const [viewInstruction, setViewInstruction] = useState(null);

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareModalCredential, setShareModalCredential] = useState(null);
  const [shareModalScope, setShareModalScope] = useState("personal");
  const [shareModalLoading, setShareModalLoading] = useState(false);
  const [shareSaving, setShareSaving] = useState(false);
  const [shareEmployeeOptions, setShareEmployeeOptions] = useState([]);
  const [shareSelectedUserIds, setShareSelectedUserIds] = useState([]);

  const [fileShareModalOpen, setFileShareModalOpen] = useState(false);
  const [fileShareModalRecord, setFileShareModalRecord] = useState(null);
  const [fileShareModalLoading, setFileShareModalLoading] = useState(false);
  const [fileShareSaving, setFileShareSaving] = useState(false);
  const [fileShareSelectedUserIds, setFileShareSelectedUserIds] = useState([]);

  const currentUserId = Number(user?.userId || user?.id || 0);
  const isAdmin = hasAdminAccess(user);

  const isFileOwner = (record) =>
    Boolean(record?.isOwner)
    || (currentUserId > 0 && Number(record?.createdBy) === currentUserId);

  const canShareFile = (record) => {
    if (!record?.id || record.sharedWithMe || fileListFilter === "shared") return false;
    if (isFileOwner(record) || isAdmin) return true;
    if (canUploadProjectFiles && record?.projectId) return true;
    return Boolean(canUploadSharedFiles && !record?.projectId && isFileOwner(record));
  };

  const projectId = selectedProject?.value;

  const projectUrl = (template) =>
    template.replace(":projectId", String(projectId));

  const projectFileUrl = (template, targetProjectId) =>
    template.replace(":projectId", String(targetProjectId));

  const fetchProjectCredentials = useCallback(async () => {
    if (!projectId) return;
    const response = await protectedApi({
      url: projectUrl(APIS.PROJECT_CREDENTIALS_LIST),
      method: "GET",
    });
    const data = response?.data?.data;
    setCredentials(Array.isArray(data?.items) ? data.items : []);
  }, [projectId]);

  const fetchInstructions = useCallback(async () => {
    if (!projectId) return;
    const response = await protectedApi({
      url: projectUrl(APIS.PROJECT_INSTRUCTIONS_LIST),
      method: "GET",
    });
    const data = response?.data?.data;
    setInstructions(Array.isArray(data?.items) ? data.items : []);
  }, [projectId]);

  const fetchResources = useCallback(async () => {
    if (!projectId) return;
    const response = await protectedApi({
      url: projectUrl(APIS.PROJECT_RESOURCES_LIST),
      method: "GET",
    });
    const data = response?.data?.data;
    setResources(Array.isArray(data?.items) ? data.items : []);
  }, [projectId]);

  const fetchProjectFiles = useCallback(async () => {
    if (!projectId) return;
    const response = await protectedApi({
      url: projectUrl(APIS.PROJECT_CREDENTIAL_FILES_LIST),
      method: "GET",
    });
    const data = response?.data?.data;
    setProjectFiles(Array.isArray(data?.items) ? data.items : []);
  }, [projectId]);

  const fetchAllProjectFiles = useCallback(async () => {
    const response = await protectedApi({
      url: APIS.PROJECT_CREDENTIAL_FILES_ALL,
      method: "GET",
    });
    const data = response?.data?.data;
    setProjectFiles(Array.isArray(data?.items) ? data.items : []);
  }, []);

  const fetchSharedWithMeFiles = useCallback(async () => {
    const url = projectId
      ? `${APIS.PROJECT_CREDENTIAL_FILES_SHARED_WITH_ME}?projectId=${projectId}`
      : APIS.PROJECT_CREDENTIAL_FILES_SHARED_WITH_ME;
    const response = await protectedApi({ url, method: "GET" });
    const data = response?.data?.data;
    setProjectFiles(Array.isArray(data?.items) ? data.items : []);
  }, [projectId]);

  const fetchPersonal = useCallback(async () => {
    const response = await protectedApi({
      url: APIS.PROJECT_CREDENTIALS_PERSONAL,
      method: "GET",
    });
    const data = response?.data?.data;
    setPersonalCredentials(Array.isArray(data?.items) ? data.items : []);
  }, []);

  const loadShareEmployeeOptions = useCallback(async () => {
    try {
      const paths = [APIS.EMPLOYEE_DIRECTORY, APIS.EMPLOYEE_DIRECTORY_FALLBACK];
      for (const url of paths) {
        try {
          const response = await protectedApi({ url, method: "GET" });
          const users = response?.data?.data?.users || response?.data?.data || [];
          setShareEmployeeOptions(
            (Array.isArray(users) ? users : [])
              .map((employee) => {
                const id = Number(employee.userId || employee.id);
                const name = `${employee.firstname || ""} ${employee.lastname || ""}`.trim();
                return {
                  value: id,
                  label: name || employee.email || `User #${id}`,
                };
              })
              .filter((option) => option.value > 0 && option.value !== currentUserId)
          );
          return;
        } catch (error) {
          if (error?.response?.status !== 404) break;
        }
      }
      setShareEmployeeOptions([]);
    } catch (error) {
      showErrorResponse(error);
    }
  }, [currentUserId]);

  useEffect(() => {
    if (
      canPersonal
      || canShareCredential
      || canReadSharedFiles
      || canUploadSharedFiles
      || canUploadProjectFiles
    ) {
      loadShareEmployeeOptions();
    }
  }, [
    canPersonal,
    canShareCredential,
    canReadSharedFiles,
    canUploadSharedFiles,
    canUploadProjectFiles,
    loadShareEmployeeOptions,
  ]);

  const loadTabData = useCallback(async () => {
    if (!canViewPage) return;
    setLoading(true);
    try {
      if (activeTab === "personal") {
        await fetchPersonal();
      } else if (activeTab === "files") {
        if (!canReadProjectFiles && !canReadSharedFiles) return;
        if (fileListFilter === "shared") {
          await fetchSharedWithMeFiles();
        } else if (canReadProjectFiles) {
          if (projectId) await fetchProjectFiles();
          else await fetchAllProjectFiles();
        } else {
          setProjectFiles([]);
        }
      } else if (projectId) {
        if (activeTab === "credentials") await fetchProjectCredentials();
        if (activeTab === "instructions") await fetchInstructions();
        if (activeTab === "resources") await fetchResources();
      } else {
        setCredentials([]);
        setInstructions([]);
        setResources([]);
      }
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setLoading(false);
    }
  }, [
    activeTab,
    canReadProjectFiles,
    canReadSharedFiles,
    canViewPage,
    fetchAllProjectFiles,
    fetchInstructions,
    fetchPersonal,
    fetchProjectCredentials,
    fetchProjectFiles,
    fetchResources,
    fetchSharedWithMeFiles,
    fileListFilter,
    projectId,
  ]);

  useEffect(() => {
    if (!canReadProjectFiles && canReadSharedFiles) {
      setFileListFilter("shared");
    }
  }, [canReadProjectFiles, canReadSharedFiles]);

  useEffect(() => {
    loadTabData();
  }, [loadTabData]);

  const revealProjectPassword = async (record) => {
    const response = await protectedApi({
      url: projectUrl(APIS.PROJECT_CREDENTIALS_BY_ID).replace(":id", String(record.id)),
      method: "GET",
    });
    const data = response?.data?.data;
    return data?.password || data?.secret || "";
  };

  const revealPersonalPassword = async (record) => {
    const response = await protectedApi({
      url: APIS.PROJECT_CREDENTIALS_PERSONAL_BY_ID.replace(":id", String(record.id)),
      method: "GET",
    });
    const data = response?.data?.data;
    return data?.password || data?.secret || "";
  };

  const openCreateModal = (type) => {
    setModalType(type);
    setModalMode("create");
    setEditingItem(null);
    if (type === "instruction") setFormData(EMPTY_INSTRUCTION);
    else if (type === "resource") setFormData(EMPTY_RESOURCE);
    else setFormData(EMPTY_CREDENTIAL);
    setModalOpen(true);
  };

  const openEditModal = async (type, record) => {
    if ((type === "personal" || type === "credential") && record?.sharedWithMe) {
      showErrorToast("You can only view credentials shared with you.");
      return;
    }
    setModalType(type);
    setModalMode("edit");
    setEditingItem(record);
    if (type === "instruction") {
      setFormData({ title: record.title || "", content: record.content || "" });
      setModalOpen(true);
      return;
    }
    if (type === "resource") {
      setFormData({
        title: record.title || "",
        url: record.url || "",
        description: record.description || "",
      });
      setModalOpen(true);
      return;
    }
    setFormData({
      label: record.label || "",
      identifierType: record.identifierType || "username",
      identifierValue: record.identifierValue || record.username || "",
      identifierOtherLabel: record.identifierOtherLabel || "",
      password: "",
      url: record.url || "",
      notes: record.notes || "",
    });
    const hasPassword = record.hasPassword ?? record.hasSecret;
    if (hasPassword) {
      try {
        const isPersonal = activeTab === "personal";
        const revealedPassword = isPersonal
          ? await revealPersonalPassword(record)
          : await revealProjectPassword(record);
        setFormData((prev) => ({ ...prev, password: revealedPassword }));
      } catch (error) {
        showErrorResponse(error);
      }
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setFormData(EMPTY_CREDENTIAL);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modalType === "credential" || modalType === "personal") {
        const label = String(formData.label || "").trim();
        if (!label) {
          showErrorToast("Label is required.");
          return;
        }
        const identifierType = formData.identifierType || "username";
        if (identifierType === "other" && !String(formData.identifierOtherLabel || "").trim()) {
          showErrorToast("Describe what this identifier is.");
          return;
        }
        const payload = {
          label,
          identifierType,
          identifierValue: formData.identifierValue || "",
          identifierOtherLabel:
            identifierType === "other"
              ? String(formData.identifierOtherLabel || "").trim()
              : "",
          url: formData.url || "",
          notes: formData.notes || "",
        };
        if (modalMode === "create" || String(formData.password || "").trim()) {
          payload.password = formData.password || "";
        }
        if (modalType === "personal") {
          if (modalMode === "create") {
            await protectedApi({
              url: APIS.PROJECT_CREDENTIALS_PERSONAL,
              method: "POST",
              data: payload,
            });
            showSuccessToast("Personal credential created.");
          } else {
            await protectedApi({
              url: APIS.PROJECT_CREDENTIALS_PERSONAL_BY_ID.replace(
                ":id",
                String(editingItem.id)
              ),
              method: "PUT",
              data: payload,
            });
            showSuccessToast("Personal credential updated.");
          }
        } else {
          if (!projectId) {
            showErrorToast("Select a project first.");
            return;
          }
          if (modalMode === "create") {
            await protectedApi({
              url: projectUrl(APIS.PROJECT_CREDENTIALS_LIST),
              method: "POST",
              data: payload,
            });
            showSuccessToast("Credential created.");
          } else {
            await protectedApi({
              url: projectUrl(APIS.PROJECT_CREDENTIALS_BY_ID).replace(
                ":id",
                String(editingItem.id)
              ),
              method: "PUT",
              data: payload,
            });
            showSuccessToast("Credential updated.");
          }
        }
      } else if (modalType === "instruction") {
        const title = String(formData.title || "").trim();
        const content = String(formData.content || "").trim();
        if (!title || !content) {
          showErrorToast("Title and content are required.");
          return;
        }
        if (!projectId) {
          showErrorToast("Select a project first.");
          return;
        }
        const payload = { title, content };
        if (modalMode === "create") {
          await protectedApi({
            url: projectUrl(APIS.PROJECT_INSTRUCTIONS_LIST),
            method: "POST",
            data: payload,
          });
          showSuccessToast("Instruction created.");
        } else {
          await protectedApi({
            url: projectUrl(APIS.PROJECT_INSTRUCTIONS_BY_ID).replace(
              ":id",
              String(editingItem.id)
            ),
            method: "PUT",
            data: payload,
          });
          showSuccessToast("Instruction updated.");
        }
      } else if (modalType === "resource") {
        const title = String(formData.title || "").trim();
        if (!title) {
          showErrorToast("Title is required.");
          return;
        }
        if (!projectId) {
          showErrorToast("Select a project first.");
          return;
        }
        const payload = {
          title,
          url: formData.url || "",
          description: formData.description || "",
        };
        if (modalMode === "create") {
          await protectedApi({
            url: projectUrl(APIS.PROJECT_RESOURCES_LIST),
            method: "POST",
            data: payload,
          });
          showSuccessToast("Resource created.");
        } else {
          await protectedApi({
            url: projectUrl(APIS.PROJECT_RESOURCES_BY_ID).replace(
              ":id",
              String(editingItem.id)
            ),
            method: "PUT",
            data: payload,
          });
          showSuccessToast("Resource updated.");
        }
      }
      closeModal();
      await loadTabData();
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type, record, scope = "project") => {
    if ((type === "personal" || type === "credential") && record?.sharedWithMe) return;
    setDeleteLoadingId(record.id);
    try {
      if (type === "personal") {
        await protectedApi({
          url: APIS.PROJECT_CREDENTIALS_PERSONAL_BY_ID.replace(":id", String(record.id)),
          method: "DELETE",
        });
        showSuccessToast("Personal credential deleted.");
      } else if (type === "credential") {
        await protectedApi({
          url: projectUrl(APIS.PROJECT_CREDENTIALS_BY_ID).replace(":id", String(record.id)),
          method: "DELETE",
        });
        showSuccessToast("Credential deleted.");
      } else if (type === "instruction") {
        await protectedApi({
          url: projectUrl(APIS.PROJECT_INSTRUCTIONS_BY_ID).replace(":id", String(record.id)),
          method: "DELETE",
        });
        showSuccessToast("Instruction deleted.");
      } else if (type === "resource") {
        await protectedApi({
          url: projectUrl(APIS.PROJECT_RESOURCES_BY_ID).replace(":id", String(record.id)),
          method: "DELETE",
        });
        showSuccessToast("Resource deleted.");
      } else if (type === "file") {
        const isShared = scope === "shared";
        const recordProjectId = record?.projectId || projectId;
        const url = isShared
          ? APIS.PROJECT_CREDENTIAL_FILE_SHARED_BY_ID.replace(":id", String(record.id))
          : projectFileUrl(APIS.PROJECT_CREDENTIAL_FILE_BY_ID, recordProjectId).replace(
              ":id",
              String(record.id)
            );
        await protectedApi({ url, method: "DELETE" });
        showSuccessToast("File deleted.");
      }
      await loadTabData();
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const openFileModal = (mode, scope, record = null) => {
    setFileModalScope(scope);
    setFileModalMode(mode);
    setEditingFile(record);
    if (mode === "edit" && record) {
      setFileFormData({
        title: record.title || "",
        fileType: record.fileType || "env",
        notes: record.notes || "",
      });
      if (scope === "project" && record.projectId) {
        setUploadTargetProject({
          value: record.projectId,
          label: record.projectName || `Project #${record.projectId}`,
        });
      } else {
        setUploadTargetProject(null);
      }
    } else {
      setFileFormData(EMPTY_FILE_FORM);
      setUploadTargetProject(scope === "project" ? selectedProject : null);
      setProjectLinkMode("list");
      setCustomReference("");
    }
    setFileUpload(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setFileModalOpen(true);
  };

  const closeFileModal = () => {
    setFileModalOpen(false);
    setEditingFile(null);
    setFileFormData(EMPTY_FILE_FORM);
    setFileUpload(null);
    setUploadTargetProject(null);
    setProjectLinkMode("list");
    setCustomReference("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const buildFileNotesPayload = () => {
    const parts = [];
    if (projectLinkMode === "other" && customReference.trim()) {
      parts.push(`Reference: ${customReference.trim()}`);
    }
    if (fileFormData.fileType === "other" && String(fileFormData.otherDescription || "").trim()) {
      parts.push(`Details: ${String(fileFormData.otherDescription).trim()}`);
    }
    if (String(fileFormData.notes || "").trim()) {
      parts.push(String(fileFormData.notes).trim());
    }
    return parts.join("\n");
  };

  const handleFileInputChange = (event) => {
    setFileUpload(event.target.files?.[0] || null);
  };

  const fileRecordScope = (record) => (record?.projectId ? "project" : "shared");

  const handleFileSave = async () => {
    const title = String(fileFormData.title || "").trim();
    if (!title) {
      showErrorToast("Title is required.");
      return;
    }
    if (fileModalMode === "create" && !fileUpload) {
      showErrorToast("Select a file to upload.");
      return;
    }

    const useCustomReference =
      fileModalMode === "create" && projectLinkMode === "other";

    if (useCustomReference) {
      if (!customReference.trim()) {
        showErrorToast("Enter a reference for this file.");
        return;
      }
      if (!canUploadProjectFiles && !canUploadSharedFiles) {
        showErrorToast("You do not have permission to upload this file.");
        return;
      }
    } else if (fileModalMode === "create" || fileRecordScope(editingFile) === "project") {
      const targetProjectId = uploadTargetProject?.value;
      if (!targetProjectId) {
        showErrorToast("Select a project for this file.");
        return;
      }
    }

    setFileSaving(true);
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("fileType", fileFormData.fileType || "env");
      const notesPayload = buildFileNotesPayload();
      if (notesPayload) formData.append("notes", notesPayload);
      if (fileUpload) formData.append("file", fileUpload);

      const isShared = useCustomReference || (fileModalMode === "edit" && !editingFile?.projectId);
      const targetProjectId = uploadTargetProject?.value;
      const url =
        fileModalMode === "create"
          ? isShared
            ? APIS.PROJECT_CREDENTIAL_FILES_SHARED
            : projectFileUrl(APIS.PROJECT_CREDENTIAL_FILES_LIST, targetProjectId)
          : isShared
            ? APIS.PROJECT_CREDENTIAL_FILE_SHARED_BY_ID.replace(":id", String(editingFile.id))
            : projectFileUrl(
                APIS.PROJECT_CREDENTIAL_FILE_BY_ID,
                editingFile?.projectId || targetProjectId
              ).replace(":id", String(editingFile.id));

      await protectedApi({
        url,
        method: fileModalMode === "create" ? "POST" : "PUT",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      showSuccessToast(
        fileModalMode === "create"
          ? useCustomReference
            ? "File uploaded. Use Share to give teammates access."
            : "File uploaded."
          : "File updated."
      );
      closeFileModal();
      if (activeTab === "files") {
        if (fileListFilter === "shared") await fetchSharedWithMeFiles();
        else if (projectId) await fetchProjectFiles();
        else await fetchAllProjectFiles();
      } else {
        await loadTabData();
      }
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setFileSaving(false);
    }
  };

  const handleFileDownload = async (record) => {
    setDownloadLoadingId(`file-${record.id}`);
    try {
      await downloadProjectCredentialFile({
        projectId: record?.projectId || null,
        fileId: record.id,
        fileName: record.fileName,
        sharedWithMe: Boolean(record.sharedWithMe),
      });
    } catch (error) {
      showErrorToast(error?.message || "Could not download file.");
    } finally {
      setDownloadLoadingId(null);
    }
  };

  const openFileShareModal = async (record) => {
    if (!canShareFile(record)) return;
    setFileShareModalRecord(record);
    setFileShareModalOpen(true);
    setFileShareModalLoading(true);
    setFileShareSelectedUserIds([]);
    try {
      const response = await protectedApi({
        url: APIS.PROJECT_CREDENTIAL_FILE_SHARED_SHARES.replace(":id", String(record.id)),
        method: "GET",
      });
      const sharedUsers = Array.isArray(response?.data?.data) ? response.data.data : [];
      setFileShareSelectedUserIds(
        sharedUsers.map((row) => Number(row.userId)).filter((id) => id > 0)
      );
    } catch (error) {
      showErrorResponse(error);
      setFileShareModalOpen(false);
      setFileShareModalRecord(null);
    } finally {
      setFileShareModalLoading(false);
    }
  };

  const closeFileShareModal = () => {
    setFileShareModalOpen(false);
    setFileShareModalRecord(null);
    setFileShareSelectedUserIds([]);
    setFileShareModalLoading(false);
  };

  const saveFileShares = async () => {
    if (!fileShareModalRecord?.id) return;
    setFileShareSaving(true);
    try {
      const response = await protectedApi({
        url: APIS.PROJECT_CREDENTIAL_FILE_SHARED_SHARES.replace(
          ":id",
          String(fileShareModalRecord.id)
        ),
        method: "PUT",
        data: { userIds: fileShareSelectedUserIds },
      });
      showSuccessToast(response?.data?.message || "Sharing updated.");
      closeFileShareModal();
      if (fileListFilter === "shared") await fetchSharedWithMeFiles();
      else if (projectId) await fetchProjectFiles();
      else await fetchAllProjectFiles();
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setFileShareSaving(false);
    }
  };

  const openShareModal = async (record, scope = "personal") => {
    if (!record?.id || record.sharedWithMe) return;
    if (scope === "personal" && !canPersonal) return;
    if (scope === "project" && !canShareCredential) return;
    setShareModalScope(scope);
    setShareModalCredential(record);
    setShareModalOpen(true);
    setShareModalLoading(true);
    setShareSelectedUserIds([]);
    try {
      const url =
        scope === "project"
          ? projectUrl(APIS.PROJECT_CREDENTIALS_SHARES).replace(":id", String(record.id))
          : APIS.PROJECT_CREDENTIALS_PERSONAL_SHARES.replace(":id", String(record.id));
      const response = await protectedApi({ url, method: "GET" });
      const sharedUsers = Array.isArray(response?.data?.data) ? response.data.data : [];
      setShareSelectedUserIds(sharedUsers.map((row) => Number(row.userId)).filter((id) => id > 0));
    } catch (error) {
      showErrorResponse(error);
      setShareModalOpen(false);
      setShareModalCredential(null);
    } finally {
      setShareModalLoading(false);
    }
  };

  const closeShareModal = () => {
    setShareModalOpen(false);
    setShareModalCredential(null);
    setShareModalScope("personal");
    setShareSelectedUserIds([]);
    setShareModalLoading(false);
  };

  const saveCredentialShares = async () => {
    if (!shareModalCredential?.id) return;
    const scope = shareModalScope;
    setShareSaving(true);
    try {
      const url =
        scope === "project"
          ? projectUrl(APIS.PROJECT_CREDENTIALS_SHARES).replace(
              ":id",
              String(shareModalCredential.id)
            )
          : APIS.PROJECT_CREDENTIALS_PERSONAL_SHARES.replace(
              ":id",
              String(shareModalCredential.id)
            );
      const response = await protectedApi({
        url,
        method: "PUT",
        data: { userIds: shareSelectedUserIds },
      });
      showSuccessToast(response?.data?.message || "Sharing updated.");
      closeShareModal();
      if (scope === "project") await fetchProjectCredentials();
      else await fetchPersonal();
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setShareSaving(false);
    }
  };

  const actionColumn = (type, canEdit, canRemove) => ({
    title: "Actions",
    key: "actions",
    width: type === "credential" ? 150 : 120,
    render: (_, record) => {
      const isSharedViewOnly = Boolean(record.sharedWithMe);
      const showShare = type === "credential" && canShareCredential && !isSharedViewOnly;
      const showEdit = canEdit && !isSharedViewOnly;
      const showDelete = canRemove && !isSharedViewOnly;
      return (
        <div className="project-credentials-page__actions">
          {showShare ? (
            <Button
              type="text"
              size="small"
              icon={<ShareAltOutlined />}
              onClick={() => openShareModal(record, "project")}
              aria-label="Share credential"
            />
          ) : null}
          {showEdit ? (
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(type, record)}
            />
          ) : null}
          {showDelete ? (
            <Popconfirm
              title="Delete this item?"
              onConfirm={() => handleDelete(type, record)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={deleteLoadingId === record.id}
              />
            </Popconfirm>
          ) : null}
        </div>
      );
    },
  });

  const identifierColumns = [
    {
      title: "Type",
      dataIndex: "identifierType",
      key: "identifierType",
      width: 130,
      render: (type, record) =>
        identifierTypeDisplay(type, record.identifierOtherLabel),
    },
    {
      title: "Value",
      key: "identifierValue",
      render: (_, record) => record.identifierValue || record.username || "—",
    },
    {
      title: "Password",
      key: "password",
      render: (_, record) => (
        <PasswordCell record={record} onReveal={revealProjectPassword} />
      ),
    },
  ];

  const credentialColumns = [
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      render: (label, record) => (
        <div className="project-credentials-page__label-cell">
          <span>{label}</span>
          {record.sharedWithMe ? <Tag color="cyan">Shared with you</Tag> : null}
          {!record.sharedWithMe && record.sharedWithCount > 0 ? (
            <Tag color="blue">
              Shared with {record.sharedWithCount} employee
              {record.sharedWithCount === 1 ? "" : "s"}
            </Tag>
          ) : null}
          {!record.sharedWithMe && !record.sharedWithCount && record.isOwner ? (
            <Tag color="default">Private</Tag>
          ) : null}
        </div>
      ),
    },
    {
      title: "Owner",
      key: "owner",
      width: 140,
      render: (_, record) =>
        record.isOwner
          ? <Text type="secondary">You</Text>
          : (record.ownerName || "—"),
    },
    ...identifierColumns,
    {
      title: "URL",
      dataIndex: "url",
      key: "url",
      render: (url) =>
        url ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            <LinkOutlined /> Open
          </a>
        ) : (
          "—"
        ),
    },
    { title: "Notes", dataIndex: "notes", key: "notes", ellipsis: true, render: (v) => v || "—" },
    actionColumn("credential", canUpdate, canDelete),
  ];

  const personalActionColumn = {
    title: "Actions",
    key: "actions",
    width: 150,
    render: (_, record) => {
      const isOwner = record.isOwner !== false && !record.sharedWithMe;
      return (
        <div className="project-credentials-page__actions">
          {canPersonal && isOwner ? (
            <Button
              type="text"
              size="small"
              icon={<ShareAltOutlined />}
              onClick={() => openShareModal(record, "personal")}
              aria-label="Share credential"
            />
          ) : null}
          {canPersonal && isOwner ? (
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal("personal", record)}
            />
          ) : null}
          {canPersonal && isOwner ? (
            <Popconfirm
              title="Delete this credential?"
              onConfirm={() => handleDelete("personal", record)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={deleteLoadingId === record.id}
              />
            </Popconfirm>
          ) : null}
        </div>
      );
    },
  };

  const personalColumns = [
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      render: (label, record) => (
        <div className="project-credentials-page__label-cell">
          <span>{label}</span>
          {record.sharedWithMe ? <Tag color="cyan">Shared with you</Tag> : null}
          {!record.sharedWithMe && record.sharedWithCount > 0 ? (
            <Tag color="blue">
              Shared with {record.sharedWithCount} employee
              {record.sharedWithCount === 1 ? "" : "s"}
            </Tag>
          ) : null}
        </div>
      ),
    },
    {
      title: "Owner",
      key: "owner",
      width: 140,
      render: (_, record) =>
        record.sharedWithMe ? record.ownerName || "—" : <Text type="secondary">You</Text>,
    },
    {
      title: "Type",
      dataIndex: "identifierType",
      key: "identifierType",
      width: 130,
      render: (type, record) =>
        identifierTypeDisplay(type, record.identifierOtherLabel),
    },
    {
      title: "Value",
      key: "identifierValue",
      render: (_, record) => record.identifierValue || record.username || "—",
    },
    {
      title: "Password",
      key: "password",
      render: (_, record) => (
        <PasswordCell record={record} onReveal={revealPersonalPassword} />
      ),
    },
    {
      title: "URL",
      dataIndex: "url",
      key: "url",
      render: (url) =>
        url ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            <LinkOutlined /> Open
          </a>
        ) : (
          "—"
        ),
    },
    { title: "Notes", dataIndex: "notes", key: "notes", ellipsis: true, render: (v) => v || "—" },
    personalActionColumn,
  ];

  const instructionColumns = [
    { title: "Title", dataIndex: "title", key: "title" },
    {
      title: "Preview",
      dataIndex: "content",
      key: "content",
      ellipsis: true,
      render: (content) => (
        <Text type="secondary" ellipsis>
          {String(content || "").replace(/<[^>]+>/g, " ").slice(0, 120)}
        </Text>
      ),
    },
    {
      title: "",
      key: "view",
      width: 80,
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => setViewInstruction(record)}>
          View
        </Button>
      ),
    },
    actionColumn("instruction", canUpdate, canDelete),
  ];

  const resourceColumns = [
    { title: "Title", dataIndex: "title", key: "title" },
    {
      title: "URL",
      dataIndex: "url",
      key: "url",
      render: (url) =>
        url ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {url}
          </a>
        ) : (
          "—"
        ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (v) => v || "—",
    },
    actionColumn("resource", canUpdate, canDelete),
  ];

  const buildFileColumns = (showProjectColumn = false, readOnlyShared = false) => {
    const columns = [
      {
        title: "Type",
        dataIndex: "fileType",
        key: "fileType",
        width: 110,
        render: (type) => <Tag>{FILE_TYPE_LABELS[type] || type}</Tag>,
      },
    ];
    if (showProjectColumn) {
      columns.push({
        title: "Project / Reference",
        key: "projectName",
        ellipsis: true,
        render: (_, record) => {
          const label = record.projectName || (record.isCustomOther ? "Other" : "—");
          return (
            <div className="project-credentials-page__label-cell">
              <span>{label}</span>
              {record.isCustomOther ? <Tag color="purple">Other</Tag> : null}
              {record.sharedWithMe ? <Tag color="cyan">Shared with you</Tag> : null}
              {isFileOwner(record) && record.sharedWithCount > 0 ? (
                <Tag color="blue">
                  Shared with {record.sharedWithCount} employee
                  {record.sharedWithCount === 1 ? "" : "s"}
                </Tag>
              ) : null}
            </div>
          );
        },
      });
    }
    columns.push({
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      render: (title, record) => (
        <div className="project-credentials-page__label-cell">
          <span>{title}</span>
          {record.sharedWithMe ? <Tag color="cyan">Shared with you</Tag> : null}
          {isFileOwner(record) && record.sharedWithCount > 0 ? (
            <Tag color="blue">
              Shared with {record.sharedWithCount} employee
              {record.sharedWithCount === 1 ? "" : "s"}
            </Tag>
          ) : null}
          {isFileOwner(record) && !record.sharedWithCount ? <Tag color="default">Private</Tag> : null}
        </div>
      ),
    });
    if (readOnlyShared) {
      columns.push({
        title: "Shared by",
        key: "sharedByName",
        ellipsis: true,
        render: (_, record) => record.sharedByName || "—",
      });
    }
    columns.push({
      title: "File",
      dataIndex: "fileName",
      key: "fileName",
      ellipsis: true,
      render: (name) => name || "—",
    });
    columns.push({
      title: "Actions",
      key: "actions",
      width: 210,
      render: (_, record) => {
        const recordScope = fileRecordScope(record);
        const owner = isFileOwner(record);
        const canEdit =
          !readOnlyShared
          && (recordScope === "shared"
            ? owner && (canUploadSharedFiles || canUploadProjectFiles)
            : canUploadProjectFiles && (owner || isAdmin));
        const canDeleteFiles =
          !readOnlyShared
          && (recordScope === "shared"
            ? owner && (canDeleteSharedFiles || canUploadProjectFiles)
            : canDeleteProjectFiles && (owner || isAdmin));
        return (
          <div className="project-credentials-page__actions">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              loading={downloadLoadingId === `file-${record.id}`}
              onClick={() => handleFileDownload(record)}
              aria-label="Download file"
            />
            {canShareFile(record) ? (
              <Button
                type="text"
                size="small"
                icon={<ShareAltOutlined />}
                onClick={() => openFileShareModal(record)}
                aria-label="Share file"
              />
            ) : null}
            {canEdit ? (
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openFileModal("edit", recordScope, record)}
              />
            ) : null}
            {canDeleteFiles ? (
              <Popconfirm
                title="Delete this file?"
                onConfirm={() => handleDelete("file", record, recordScope)}
                okText="Delete"
                okButtonProps={{ danger: true }}
              >
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleteLoadingId === record.id}
                />
              </Popconfirm>
            ) : null}
          </div>
        );
      },
    });
    return columns;
  };

  const renderAddButton = () => {
    if (activeTab === "personal" && canPersonal) {
      return (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreateModal("personal")}>
          Add credential
        </Button>
      );
    }
    if (activeTab === "files") {
      if (!canUploadProjectFiles && !canUploadSharedFiles) return null;
      if (fileListFilter === "shared") return null;
      return (
        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={() => openFileModal("create", "project")}
        >
          Upload file
        </Button>
      );
    }
    if (!projectId) return null;
    if (activeTab === "credentials" && canCreate) {
      return (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreateModal("credential")}>
          Add credential
        </Button>
      );
    }
    if (activeTab === "instructions" && canCreate) {
      return (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreateModal("instruction")}>
          Add instruction
        </Button>
      );
    }
    if (activeTab === "resources" && canCreate) {
      return (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreateModal("resource")}>
          Add resource
        </Button>
      );
    }
    return null;
  };

  const tabItems = [
    ...(canRead ? [{ key: "credentials", label: "Credentials" }] : []),
    ...(canRead ? [{ key: "instructions", label: "Instructions" }] : []),
    ...(canViewFiles ? [{ key: "files", label: "Files" }] : []),
    ...(canPersonal ? [{ key: "personal", label: "My Credentials" }] : []),
  ];

  useEffect(() => {
    const keys = tabItems.map((tab) => tab.key);
    if (keys.length && !keys.includes(activeTab)) {
      setActiveTab(keys[0]);
    }
  }, [activeTab, tabItems]);

  const modalTitle = () => {
    const action = modalMode === "create" ? "Add" : "Edit";
    const labels = {
      credential: "credential",
      personal: "personal credential",
      instruction: "instruction",
      resource: "resource",
    };
    return `${action} ${labels[modalType] || "item"}`;
  };

  if (!canViewPage) {
    return (
      <div className="project-credentials-page">
        <p>You do not have permission to view project credentials.</p>
      </div>
    );
  }

  return (
    <div className="project-credentials-page">
      <div className="project-credentials-page__intro">
        <h1 className="page-title">Project Credentials</h1>
        <p className="page-subtitle">
          Shared credentials, setup instructions, and project files (.env, .pem, Postman JSON).
          Store personal credentials in My Credentials and share them with teammates.
        </p>
      </div>

      <div className="project-credentials-page__toolbar">
        {activeTab !== "personal" && activeTab !== "files" ? (
          <div className="project-credentials-page__project-select">
            <ProjectSelect
              value={selectedProject}
              onChange={setSelectedProject}
              placeholder="Select a project"
              showLabel
            />
          </div>
        ) : activeTab === "files" ? (
          <div className="project-credentials-page__files-toolbar">
            <div className="project-credentials-page__project-select">
              <ProjectSelect
                value={selectedProject}
                onChange={setSelectedProject}
                placeholder="All projects"
                showLabel
                isClearable
              />
              {fileListFilter === "all" && !projectId ? (
                <Text type="secondary" className="project-credentials-page__file-hint">
                  Showing all project files you can access. Select a project to filter.
                </Text>
              ) : null}
            </div>
            <div className="project-credentials-page__file-filter">
              <label>View</label>
              <Select
                value={fileListFilter}
                onChange={setFileListFilter}
                options={[
                  ...(canReadProjectFiles ? [{ value: "all", label: "All files" }] : []),
                  { value: "shared", label: "Shared with me" },
                ]}
                style={{ minWidth: 160 }}
              />
            </div>
          </div>
        ) : null}
        <div className="project-credentials-page__toolbar-actions">{renderAddButton()}</div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />

      <Spin spinning={loading}>
        {activeTab === "files" ? (
          <div className="project-credentials-page__files-tab">
            <Table
              rowKey="id"
              dataSource={projectFiles}
              columns={buildFileColumns(
                fileListFilter === "all" ? !projectId : true,
                fileListFilter === "shared"
              )}
              pagination={{ pageSize: 10 }}
              locale={{
                emptyText:
                  fileListFilter === "shared"
                    ? "No files have been shared with you yet."
                    : projectId
                      ? "No project files yet."
                      : "No project files yet. Upload a file or select a project to filter.",
              }}
            />
          </div>
        ) : activeTab !== "personal" && !projectId ? (
          <div className="project-credentials-page__empty">
            <Text type="secondary">Select a project to view {activeTab}.</Text>
          </div>
        ) : (
          <Table
            rowKey="id"
            dataSource={
              activeTab === "credentials"
                ? credentials
                : activeTab === "instructions"
                  ? instructions
                  : activeTab === "resources"
                    ? resources
                    : personalCredentials
            }
            columns={
              activeTab === "credentials"
                ? credentialColumns
                : activeTab === "instructions"
                  ? instructionColumns
                  : activeTab === "resources"
                    ? resourceColumns
                    : personalColumns
            }
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: "No records yet." }}
          />
        )}
      </Spin>

      <Modal
        title={modalTitle()}
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSave}
        confirmLoading={saving}
        okText={modalMode === "create" ? "Create" : "Save"}
        destroyOnClose
        width={modalType === "instruction" ? 720 : 520}
      >
        {(modalType === "credential" || modalType === "personal") && (
          <div className="project-credentials-page__form">
            <label>Label</label>
            <Input
              value={formData.label}
              onChange={(e) => setFormData((p) => ({ ...p, label: e.target.value }))}
              placeholder="e.g. Staging server SSH"
            />
            <label>Store as</label>
            <Select
              value={formData.identifierType || "username"}
              onChange={(value) =>
                setFormData((p) => ({
                  ...p,
                  identifierType: value,
                  identifierOtherLabel: value === "other" ? p.identifierOtherLabel : "",
                }))
              }
              options={IDENTIFIER_TYPE_OPTIONS}
              style={{ width: "100%" }}
            />
            {formData.identifierType === "other" ? (
              <>
                <label>Describe this identifier</label>
                <Input
                  value={formData.identifierOtherLabel}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, identifierOtherLabel: e.target.value }))
                  }
                  placeholder="e.g. API key ID, Account number, Client code"
                />
              </>
            ) : null}
            <label>
              {formData.identifierType === "other"
                ? String(formData.identifierOtherLabel || "").trim() || "Value"
                : IDENTIFIER_TYPE_LABELS[formData.identifierType] || "Username"}
            </label>
            <Input
              value={formData.identifierValue}
              onChange={(e) => setFormData((p) => ({ ...p, identifierValue: e.target.value }))}
              placeholder={identifierPlaceholder(formData.identifierType)}
            />
            <label>Password</label>
            <Input.Password
              value={formData.password}
              onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
              placeholder={modalMode === "edit" ? "Leave blank to keep unchanged" : "Optional"}
            />
            <label>URL</label>
            <Input
              value={formData.url}
              onChange={(e) => setFormData((p) => ({ ...p, url: e.target.value }))}
              placeholder="https://..."
            />
            <label>Notes</label>
            <TextArea
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
              placeholder="Optional notes"
            />
          </div>
        )}

        {modalType === "instruction" && (
          <div className="project-credentials-page__form">
            <label>Title</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
            />
            <label>Content</label>
            <TextArea
              rows={10}
              value={formData.content}
              onChange={(e) => setFormData((p) => ({ ...p, content: e.target.value }))}
              placeholder="Setup steps, environment details, etc."
            />
          </div>
        )}

        {modalType === "resource" && (
          <div className="project-credentials-page__form">
            <label>Title</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
            />
            <label>URL</label>
            <Input
              value={formData.url}
              onChange={(e) => setFormData((p) => ({ ...p, url: e.target.value }))}
              placeholder="https://..."
            />
            <label>Description</label>
            <TextArea
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
            />
          </div>
        )}
      </Modal>

      <Modal
        title={fileModalMode === "create" ? "Upload project file" : "Edit file"}
        open={fileModalOpen}
        onCancel={closeFileModal}
        onOk={handleFileSave}
        confirmLoading={fileSaving}
        okText={fileModalMode === "create" ? "Upload" : "Save"}
        destroyOnClose
        width={520}
      >
        <div className="project-credentials-page__form">
          {fileModalMode === "create" ? (
            <>
              <label>Project assignment</label>
              <Select
                value={projectLinkMode}
                onChange={(value) => {
                  setProjectLinkMode(value);
                  if (value === "list") {
                    setUploadTargetProject(selectedProject);
                    setCustomReference("");
                  } else {
                    setUploadTargetProject(null);
                  }
                }}
                options={[
                  { value: "list", label: "Select from project list" },
                  { value: "other", label: "Other (not in project list)" },
                ]}
                style={{ width: "100%" }}
              />
            </>
          ) : null}
          {fileModalMode === "edit" || projectLinkMode === "list" ? (
            <>
              <label>Project</label>
              <ProjectSelect
                value={uploadTargetProject}
                onChange={setUploadTargetProject}
                placeholder="Select project for this file"
                showLabel={false}
                isClearable={false}
                isDisabled={fileModalMode === "edit"}
              />
              {fileModalMode === "create" ? (
                <Text type="secondary" className="project-credentials-page__file-hint">
                  You can choose a different project than the one selected in the list above.
                </Text>
              ) : null}
            </>
          ) : (
            <>
              <label>Reference / label</label>
              <Input
                value={customReference}
                onChange={(e) => setCustomReference(e.target.value)}
                placeholder="e.g. Client vendor, external server, personal sandbox"
              />
              <Text type="secondary" className="project-credentials-page__file-hint">
                Saved with this reference. Use Share on the file row to give teammates access.
              </Text>
            </>
          )}
          <label>File type</label>
          <Select
            value={fileFormData.fileType}
            onChange={(value) => {
              setFileFormData((prev) => ({ ...prev, fileType: value, otherDescription: "" }));
              setFileUpload(null);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
            options={FILE_TYPE_OPTIONS}
            style={{ width: "100%" }}
            disabled={fileModalMode === "edit"}
          />
          {fileFormData.fileType === "other" ? (
            <>
              <label>Describe this file</label>
              <Input
                value={fileFormData.otherDescription}
                onChange={(e) =>
                  setFileFormData((prev) => ({ ...prev, otherDescription: e.target.value }))
                }
                placeholder="e.g. Docker compose, nginx config, API spec"
              />
            </>
          ) : null}
          <label>Title</label>
          <Input
            value={fileFormData.title}
            onChange={(e) => setFileFormData((prev) => ({ ...prev, title: e.target.value }))}
            placeholder="e.g. Staging .env"
          />
          <label>{fileModalMode === "create" ? "File" : "Replace file (optional)"}</label>
          <input
            ref={fileInputRef}
            type="file"
            accept={FILE_TYPE_ACCEPT[fileFormData.fileType] || FILE_TYPE_ACCEPT.other}
            onChange={handleFileInputChange}
            className="project-credentials-page__file-input"
          />
          {fileUpload ? (
            <Text type="secondary" className="project-credentials-page__file-name">
              Selected: {fileUpload.name}
            </Text>
          ) : fileModalMode === "edit" && editingFile?.fileName ? (
            <Text type="secondary" className="project-credentials-page__file-name">
              Current: {editingFile.fileName}
            </Text>
          ) : null}
          <label>Notes</label>
          <TextArea
            rows={3}
            value={fileFormData.notes}
            onChange={(e) => setFileFormData((prev) => ({ ...prev, notes: e.target.value }))}
            placeholder="Optional notes"
          />
        </div>
      </Modal>

      <Modal
        title={viewInstruction?.title || "Instruction"}
        open={Boolean(viewInstruction)}
        onCancel={() => setViewInstruction(null)}
        footer={[
          <Button key="close" onClick={() => setViewInstruction(null)}>
            Close
          </Button>,
        ]}
        width={720}
      >
        <Paragraph className="project-credentials-page__instruction-view">
          {viewInstruction?.content}
        </Paragraph>
      </Modal>

      <Modal
        title={
          shareModalCredential?.label
            ? `Share: ${shareModalCredential.label}`
            : "Share credential"
        }
        open={shareModalOpen}
        onCancel={closeShareModal}
        footer={[
          <Button key="cancel" onClick={closeShareModal}>
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={shareSaving} onClick={saveCredentialShares}>
            Save sharing
          </Button>,
        ]}
        destroyOnClose
        width={560}
      >
        {shareModalLoading ? (
          <div className="project-credentials-page__share-loading">
            <Spin />
          </div>
        ) : (
          <>
            <p className="project-credentials-page__share-hint">
              {shareModalScope === "project"
                ? "Choose employees who can view this project credential. They can reveal the password but cannot edit, delete, or reshare it. Project members without access will not see it."
                : "Choose employees who can view this credential in their My Credentials tab. They can reveal the password but cannot edit or delete it."}
            </p>
            <label className="project-credentials-page__share-label">Share with employees</label>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Select employees"
              className="project-credentials-page__share-select"
              value={shareSelectedUserIds}
              onChange={(values) => setShareSelectedUserIds(values)}
              options={shareEmployeeOptions}
            />
          </>
        )}
      </Modal>

      <Modal
        title={
          fileShareModalRecord?.title
            ? `Share file: ${fileShareModalRecord.title}`
            : "Share file"
        }
        open={fileShareModalOpen}
        onCancel={closeFileShareModal}
        footer={[
          <Button key="cancel" onClick={closeFileShareModal}>
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={fileShareSaving} onClick={saveFileShares}>
            Save sharing
          </Button>,
        ]}
        destroyOnClose
        width={560}
      >
        {fileShareModalLoading ? (
          <div className="project-credentials-page__share-loading">
            <Spin />
          </div>
        ) : (
          <>
            <p className="project-credentials-page__share-hint">
              Choose employees who can view and download this file in their Shared with me list.
            </p>
            <label className="project-credentials-page__share-label">Share with employees</label>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Select employees"
              className="project-credentials-page__share-select"
              value={fileShareSelectedUserIds}
              onChange={(values) => setFileShareSelectedUserIds(values)}
              options={shareEmployeeOptions}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default ProjectCredentialsPage;
